/**
 * Slice 19d — "Browse indexed ads to link more exemplars" picker.
 * Selection state is owned by the parent (template's example_social_post_ids).
 * Checkboxes are bound to `selectedIds`; rows already in the set are persistently
 * shown as checked even when filters page past them. Capped by `maxSelected`.
 */
import { useState } from "react";
import { useAdsIndexedPostPicker } from "@/components/ads-assistant/useAdsIndexedPostPicker";

type Props = {
  scopedOrgId: string | null;
  selectedIds: string[];
  onToggle: (id: string) => void;
  maxSelected: number;
};

export function AdsLibraryIndexedPostPicker({ scopedOrgId, selectedIds, onToggle, maxSelected }: Props) {
  const vm = useAdsIndexedPostPicker(scopedOrgId);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  function toggleExpand(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  if (!scopedOrgId) return null;
  const selectedSet = new Set(selectedIds);
  const atCap = selectedIds.length >= maxSelected;

  return (
    <div className="mt-2 space-y-2 rounded-lg border border-violet-200/70 bg-violet-50/30 p-2 dark:border-violet-900/50 dark:bg-violet-950/15">
      <div className="grid gap-2 sm:grid-cols-2">
        <label className="block text-[11px] font-medium text-violet-900 dark:text-violet-200">
          Ads platform
          <select
            className="mt-0.5 w-full rounded border border-violet-200/80 bg-white px-2 py-1 text-xs dark:border-violet-900/50 dark:bg-zinc-950 dark:text-gray-100"
            value={vm.platform}
            onChange={(e) => {
              vm.setPlatform(e.target.value);
              vm.resetPaging();
            }}
          >
            {vm.platformOptions.map((o) => (
              <option key={o.key} value={o.key}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-[11px] font-medium text-violet-900 dark:text-violet-200">
          Location
          <select
            className="mt-0.5 w-full rounded border border-violet-200/80 bg-white px-2 py-1 text-xs dark:border-violet-900/50 dark:bg-zinc-950 dark:text-gray-100"
            value={vm.locationId}
            onChange={(e) => {
              vm.setLocationId(e.target.value);
              vm.resetPaging();
            }}
          >
            <option value="">All locations</option>
            {vm.locations.map((loc) => (
              <option key={loc.id} value={loc.id}>
                {loc.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="flex flex-wrap items-end gap-2">
        <label className="block min-w-[180px] flex-1 text-[11px] font-medium text-violet-900 dark:text-violet-200">
          Search post text
          <input
            className="mt-0.5 w-full rounded border border-violet-200/80 bg-white px-2 py-1 text-xs dark:border-violet-900/50 dark:bg-zinc-950 dark:text-gray-100"
            value={vm.searchDraft}
            onChange={(e) => vm.setSearchDraft(e.target.value)}
            placeholder="e.g. steel structures"
          />
        </label>
        <button
          type="button"
          className="rounded border border-violet-300 bg-violet-50 px-2 py-1 text-[11px] font-medium text-violet-900 hover:bg-violet-100 dark:border-violet-800 dark:bg-violet-950/50 dark:text-violet-100 dark:hover:bg-violet-950"
          onClick={() => {
            vm.setSearchApplied(vm.searchDraft);
            vm.resetPaging();
          }}
        >
          Search
        </button>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 text-[11px] text-violet-900 dark:text-violet-200">
        <span>
          {selectedIds.length}/{maxSelected} linked · Page {vm.page + 1}/{vm.pageCount} ({vm.total} matches)
        </span>
        <div className="flex gap-1">
          <button
            type="button"
            disabled={vm.page <= 0 || vm.listLoading}
            className="rounded border border-violet-200 px-2 py-0.5 text-[11px] disabled:opacity-40 dark:border-violet-800"
            onClick={() => vm.setPage((p) => Math.max(0, p - 1))}
          >
            Prev
          </button>
          <button
            type="button"
            disabled={vm.page >= vm.pageCount - 1 || vm.listLoading}
            className="rounded border border-violet-200 px-2 py-0.5 text-[11px] disabled:opacity-40 dark:border-violet-800"
            onClick={() => vm.setPage((p) => Math.min(vm.pageCount - 1, p + 1))}
          >
            Next
          </button>
        </div>
      </div>

      {vm.listError ? (
        <p className="rounded border border-amber-200 bg-amber-50/90 px-2 py-1 text-xs text-amber-950 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-100">
          {vm.listError}
        </p>
      ) : null}

      <ul className="max-h-56 space-y-1 overflow-y-auto rounded border border-violet-100 bg-white p-2 dark:border-violet-900/40 dark:bg-zinc-900/40">
        {vm.listLoading ? <li className="text-xs text-gray-500">Loading…</li> : null}
        {!vm.listLoading && !vm.posts.length ? (
          <li className="text-xs text-gray-500">No posts for these filters.</li>
        ) : null}
        {vm.posts.map((p) => {
          const checked = selectedSet.has(p.id);
          const excerpt = (p.text_content || "").replace(/\s+/g, " ").trim().slice(0, 140);
          const isExpanded = expanded.has(p.id);
          const full = (p.text_content || "").trim() || "(empty)";
          return (
            <li key={p.id} className="rounded border border-white/80 bg-white dark:border-zinc-700 dark:bg-zinc-950">
              <div className="flex items-start gap-1 px-2 py-1">
                <label className="flex min-w-0 flex-1 cursor-pointer items-start gap-2">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={checked}
                    onChange={() => onToggle(p.id)}
                    disabled={!checked && atCap}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="font-mono text-[10px] text-gray-500">{p.id.slice(0, 8)}…</span>
                    {p.template_example_guard ? (
                      <span className="ml-1 rounded bg-amber-100 px-1 py-0.5 text-[9px] font-semibold text-amber-950 dark:bg-amber-950/60 dark:text-amber-100">
                        Example guard
                      </span>
                    ) : null}
                    <span className="mt-0.5 block text-gray-800 dark:text-gray-200">
                      {isExpanded ? (
                        <span className="text-gray-500 italic dark:text-gray-400">Full text below.</span>
                      ) : (
                        excerpt || "(empty)"
                      )}
                    </span>
                  </span>
                </label>
                <button
                  type="button"
                  className="shrink-0 rounded border border-violet-200 px-1.5 py-0.5 text-[11px] font-medium text-violet-900 hover:bg-violet-50 dark:border-violet-800 dark:text-violet-200 dark:hover:bg-violet-950/50"
                  aria-expanded={isExpanded}
                  aria-label={isExpanded ? "Collapse full post text" : "Expand full post text"}
                  title={isExpanded ? "Collapse" : "Full text"}
                  onClick={() => toggleExpand(p.id)}
                >
                  ⋯
                </button>
              </div>
              {isExpanded ? (
                <pre className="max-h-40 overflow-auto whitespace-pre-wrap break-words border-t border-violet-100 px-2 py-1.5 text-[11px] text-gray-800 dark:border-violet-900/40 dark:text-gray-200">
                  {full}
                </pre>
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
